import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Observability Dashboard (010 US3 T015) — polled view of heartbeats/jobs/exports
 * and per-job trace drill-down. Role-scoped: super-admin sees all tenants,
 * tenant-admin sees own (server enforces; UI mirrors).
 *
 * Updates by polling GET /api/dashboard/activity on a client-configurable
 * interval (default 1 minute, persisted per browser) — no live SSE connection.
 * The JsonClient normalizes the snake_case wire shapes to the camelCase
 * telemetry view-models and merges in exports, so this view renders fields
 * directly.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { useSessionStore } from "../stores/session.js";
import { cacheEpoch, readCache, writeCache } from "../stores/cache.js";
import { deviceLabel, enumLabel } from "../format/labels.js";
import { CopyButton } from "../ui/CopyButton.js";
import { Icon } from "../ui/Icon.js";
import { BusyButton } from "../ui/BusyButton.js";
import { Onboarding } from "../ui/Onboarding.js";
import { Drawer } from "../ui/Drawer.js";
import type {
  DashboardSummary, ExportRecord, HeartbeatPayload, JobRecord, JobStatus, TraceEvent,
} from "@genz/platform";
import "./dashboard.css";
import { RowAction } from "../ui/RowAction.js";

/** Cached dashboard activity snapshot — seeds the view INSTANTLY on every tab
 *  re-open (cache-first), then the poll quietly swaps in any real change. */
const DASH_ACTIVITY_CACHE = "dash.activity";
type DashActivity = { jobs: JobRecord[]; heartbeats: HeartbeatPayload[]; exports: ExportRecord[] };

/** Cached per-tenant summary (super-admin only) — same cache-first treatment so
 *  the tenant table + filter populate instantly, then revalidate quietly. */
const DASH_STORES_CACHE = "dash.stores";
/** Shape returned by `client.dashboardStores()` (GET /api/dashboard/stores).
 *  Mirrors the StoreRow used in the admin Customers grid; we read a compact
 *  subset here (email · tier · devices · jobs) and use tenant_id for filtering. */
type StoreSummary = {
  customer_id: string;
  email: string;
  tenant_id: string | null;
  tier: string;
  device_count: number;
  job_count: number;
};
const TENANT_SUMMARY_LIMIT = 8;

// Refresh-interval choices offered to the admin; the selection is saved per
// browser so each client controls how often it pulls fresh status.
const REFRESH_KEY = "genz.dashboard.refreshMs";
const DEFAULT_REFRESH_MS = 60_000; // 1 minute
const REFRESH_OPTIONS: { label: string; ms: number }[] = [
  { label: "10 seconds", ms: 10_000 },
  { label: "30 seconds", ms: 30_000 },
  { label: "1 minute", ms: 60_000 },
  { label: "5 minutes", ms: 300_000 },
  { label: "15 minutes", ms: 900_000 },
];

// Map each job status to the badge tone already defined in theme.css so the
// pills read consistently with the rest of the console.
const STATUS_TONE: Record<JobStatus, string> = {
  queued: "badge--neutral",
  running: "badge--info",
  paused: "badge--warn",
  completed: "badge--success",
  failed: "badge--danger",
  canceled: "badge--neutral",
  interrupted: "badge--danger",
};

function loadRefreshMs(): number {
  if (typeof localStorage === "undefined") return DEFAULT_REFRESH_MS;
  const raw = Number(localStorage.getItem(REFRESH_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_REFRESH_MS;
}

function fmtDuration(ms?: number): string {
  if (!ms || ms <= 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function fmtAgo(ms: number, now: number): string {
  if (!ms) return "never";
  const diff = Math.max(0, now - ms);
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ms).toLocaleDateString();
}

function StatusPill({ status }: { status: JobStatus }) {
  return (
    // WS-B B-3 sweep: the LABEL is humanised; `data-status` keeps the raw value, which is
    // what the CSS and three tests key on.
    <span className={`badge ${STATUS_TONE[status] ?? "badge--neutral"}`} data-status={status}>
      {enumLabel(status)}
    </span>
  );
}

export function Dashboard() {
  const { client } = useServices();
  const isAdmin = useSessionStore((s) => s.isAdmin());
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  // Cache-first: seed from the last-known snapshot so re-opening the tab shows data
  // immediately (no empty flash); the poll below revalidates and only swaps on change.
  const cached = readCache<DashActivity>(DASH_ACTIVITY_CACHE);
  const [jobs, setJobs] = useState<JobRecord[]>(cached?.jobs ?? []);
  const [heartbeats, setHeartbeats] = useState<HeartbeatPayload[]>(cached?.heartbeats ?? []);
  const [exportsList, setExportsList] = useState<ExportRecord[]>(cached?.exports ?? []);
  // Server-computed KPI totals over the WHOLE dataset (AUD-4 IMP-1). `null` whenever the
  // cloud cannot supply them (pre-WS-G deploy, or an older @genz/platform with no method),
  // in which case the strip falls back to counting the page it already fetched.
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [selected, setSelected] = useState<{ job: JobRecord; traces: TraceEvent[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A /summary failure that is NOT the 404 compat case (review-1 M-6). Separate from
  // `error` so the parallel activity poll's `setError(null)` cannot swallow it.
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [refreshMs, setRefreshMs] = useState<number>(loadRefreshMs);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  // Super-admin tenant scope: "" = all tenants. The filter select drives which
  // tenant the activity poll requests (server already supports ?tenant=…).
  const [tenant, setTenant] = useState<string>("");
  // Per-tenant summary list (super-admin only) — seeds the tenant table + filter.
  const [stores, setStores] = useState<StoreSummary[]>(
    () => (isSuper ? readCache<StoreSummary[]>(DASH_STORES_CACHE) ?? [] : []),
  );

  const loadActivity = useCallback(async () => {
    // The optional call is the compatibility path: a cloud that predates /summary 404s,
    // and an older @genz/platform has no method at all — both leave the strip on the
    // page-derived counts rather than blanking it.
    //
    // ONLY a 404 means that (review-1 M-6). A 500 or a network failure used to take the
    // same silent branch, so the strip quietly showed the truncated-page numbers IMP-1
    // exists to remove, with nothing on screen to say the totals were not the server's.
    // Kept off the activity `error` state so the two requests stay parallel and neither
    // clobbers the other's message.
    void client.dashboardSummary?.(tenant || undefined)
      .then((s) => { setSummary(s); setSummaryError(null); })
      .catch((e: unknown) => {
        setSummary(null);
        const status = (e as { status?: unknown } | null)?.status;
        setSummaryError(status === 404
          ? null
          : `KPI totals unavailable — showing counts from the rows below. ${
            e instanceof Error ? e.message : String(e)}`);
      });
    const epoch = cacheEpoch();
    try {
      const activity = await client.dashboardActivity(tenant ? { tenant } : undefined);
      writeCache(DASH_ACTIVITY_CACHE, {
        jobs: activity.jobs, heartbeats: activity.heartbeats, exports: activity.exports,
      }, epoch);
      setJobs(activity.jobs);
      setHeartbeats(activity.heartbeats);
      setExportsList(activity.exports);
      setLastUpdated(Date.now());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client, tenant]);

  // Per-tenant summaries surface the otherwise-unused stores endpoint; fetched
  // once for super-admins (cache-first), independent of the activity poll.
  const loadStores = useCallback(async () => {
    const epoch = cacheEpoch();
    try {
      const rows = (await client.dashboardStores()) as StoreSummary[];
      writeCache(DASH_STORES_CACHE, rows, epoch);
      setStores(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  // Initial load + polling on the chosen interval (re-armed when it or the
  // tenant scope changes, so switching tenants refilters immediately).
  useEffect(() => {
    if (!isAdmin) return;
    void loadActivity();
    const id = setInterval(() => void loadActivity(), refreshMs);
    return () => clearInterval(id);
  }, [isAdmin, loadActivity, refreshMs]);

  // Super-admins also pull the per-tenant summary list (drives the table + filter).
  useEffect(() => {
    if (!isSuper) return;
    void loadStores();
  }, [isSuper, loadStores]);

  const onRefreshChange = (ms: number) => {
    setRefreshMs(ms);
    if (typeof localStorage !== "undefined") localStorage.setItem(REFRESH_KEY, String(ms));
  };

  const openJob = async (jobId: string) => {
    try {
      const detail = await client.dashboardJob(jobId);
      setSelected({ job: detail.job, traces: detail.traces });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Top-line counters for the KPI strip. Server totals when the cloud can compute them
  // (AUD-4 IMP-1: these used to be derived from /activity's 200-row page, so a workspace
  // with 40 000 jobs was told it had 200 and a 100 % success rate); otherwise the old
  // page-derived counts — a new desktop against a cloud that predates /summary must still
  // show a strip.
  const kpis = useMemo(() => {
    const online = heartbeats.filter((h) => h.online).length;
    const queued = jobs.filter((j) => j.status === "queued").length;
    const running = jobs.filter((j) => j.status === "running").length;
    const completed = jobs.filter((j) => j.status === "completed").length;
    const failed = jobs.filter((j) => j.status === "failed" || j.status === "interrupted").length;
    // Success rate over terminal outcomes only; "—" until at least one job ends.
    const terminal = completed + failed;
    const successRate = terminal > 0 ? `${Math.round((completed / terminal) * 100)}%` : "—";
    const page = {
      online, total: jobs.length, queued, running, failed, successRate,
      exports: exportsList.length,
    };
    if (!summary) return page;
    return {
      online: summary.online_desktops,
      total: summary.jobs_total,
      queued: summary.jobs_queued,
      running: summary.jobs_running,
      failed: summary.jobs_failed + summary.jobs_interrupted,
      successRate: summary.success_rate == null
        ? "—"
        : `${Math.round(summary.success_rate * 100)}%`,
      exports: summary.exports_total,
    };
  }, [heartbeats, jobs, exportsList, summary]);

  // Activity feed lines — derived from the already-fetched jobs (no extra
  // requests, no invented data). Each line carries a tone for its leading dot:
  // completed→success, running→info, failed/interrupted→danger, else→warn.
  const feed = useMemo(() => {
    type Line = { id: string; tone: "success" | "info" | "danger" | "warn"; when: number; text: string; code?: string };
    const toneFor = (s: JobStatus): Line["tone"] =>
      s === "completed" ? "success"
        : s === "running" ? "info"
        : s === "failed" || s === "interrupted" ? "danger"
        : "warn";
    const lines: Line[] = jobs
      .map((j) => ({
        id: j.jobId,
        tone: toneFor(j.status),
        when: j.completedAt ?? j.startedAt ?? j.queuedAt,
        text: `${enumLabel(j.status)} · ${j.designCount} designs`,
        code: j.jobId.slice(0, 8),
      }))
      .sort((a, b) => b.when - a.when)
      .slice(0, 8);
    return lines;
  }, [jobs]);

  // Tenant options for the super-admin filter, derived from the stores list.
  // De-duped by tenant_id; null/blank tenants are skipped (can't be filtered).
  const tenantOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of stores) {
      if (s.tenant_id && !seen.has(s.tenant_id)) seen.set(s.tenant_id, s.email);
    }
    return Array.from(seen, ([id, email]) => ({ id, email }));
  }, [stores]);

  if (!isAdmin) return <p data-testid="dashboard-denied">Admin access required.</p>;

  return (
    <section className="dashboard" data-testid="dashboard">
      {/* WS-Z4 rule: ONE page title and ONE controls row. The `<h1 className="panel-title">
          Dashboard</h1>` + sub-line live on the section that owns the five tabs
          (`observability/ObservabilitySection.tsx`); this view rendered a SECOND `.panel-head`
          under it carrying nothing but another sub-line, so the board opened with two stacked
          descriptions of itself above the first number. What survives is what is genuinely
          this view's own — the controls. */}
      <div className="dashboard__controls">
        <span className="dashboard__scope badge badge--info">
          {isSuper ? "All tenants" : "Your organization"}
        </span>
        {isSuper && tenantOptions.length > 0 && (
          <label className="dashboard__refresh-label">
            Tenant
            <select
              data-testid="dashboard-tenant-filter"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
            >
              <option value="">All tenants</option>
              {tenantOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.email}</option>
              ))}
            </select>
          </label>
        )}
        <label className="dashboard__refresh-label">
          Auto-refresh
          <select
            data-testid="dashboard-refresh"
            value={refreshMs}
            onChange={(e) => onRefreshChange(Number(e.target.value))}
          >
            {REFRESH_OPTIONS.map((o) => (
              <option key={o.ms} value={o.ms}>{o.label}</option>
            ))}
          </select>
        </label>
        <BusyButton className="btn btn--sm btn--ghost" onAction={() => loadActivity()} data-testid="dashboard-refresh-now">
          Refresh
        </BusyButton>
        <span className="dashboard__updated" data-testid="dashboard-live" aria-live="polite">
          {lastUpdated ? `Updated ${new Date(lastUpdated).toLocaleTimeString()}` : "Loading…"}
        </span>
      </div>

      <Onboarding
        sequenceKey="dashboard"
        steps={[
          { title: "Live activity", body: "This view polls your desktops, jobs and exports. Pick how often it refreshes from the Auto-refresh menu." },
          { title: "Drill into a run", body: "Use Trace on any job to see its step-by-step timeline, and the copy icon next to a Job ID to grab it for a ticket." },
        ]}
      />

      {(error ?? summaryError) && <p role="alert"><ErrorNotice detail={error ?? summaryError} /></p>}

      <div className="dashboard__kpis">
        <div className="kpi-card">
          <span className="kpi-card__label">Online desktops</span>
          <span className="kpi-card__value">{kpis.online}</span>
          <span className="dashboard__kpi-note">live heartbeats</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Jobs total</span>
          <span className="kpi-card__value" data-testid="dashboard-kpi-total">{kpis.total}</span>
          <span className="dashboard__kpi-note">{kpis.running} running</span>
        </div>
        <div className="kpi-card" data-testid="dashboard-kpi-queued">
          <span className="kpi-card__label">Queued</span>
          <span className="kpi-card__value">{kpis.queued}</span>
          <span className="dashboard__kpi-note">in line</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Running</span>
          <span className="kpi-card__value">{kpis.running}</span>
          <span className="dashboard__kpi-note">native engine</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Failed</span>
          <span className="kpi-card__value">{kpis.failed}</span>
          {/* MI-9 / MIN-10: "Failed 0 ↑ none" was a green UP ARROW on a zero. A count above
              zero is a STATE worth a tone, not a trend worth an arrow; a zero says nothing at
              all, which is the honest thing for it to say. */}
          {kpis.failed > 0 && (
            <span className="dashboard__kpi-note dashboard__kpi-note--danger">needs review</span>
          )}
        </div>
        <div className="kpi-card" data-testid="dashboard-kpi-success">
          <span className="kpi-card__label">Success rate</span>
          <span className="kpi-card__value" data-testid="dashboard-kpi-success">{kpis.successRate}</span>
          {/* MIN-10: "Success rate — ↑ terminal jobs" captioned a value that does not exist
              yet. An em dash means "not computable"; a caption under it would be a claim. */}
          {kpis.successRate !== "—" && <span className="dashboard__kpi-note">terminal jobs</span>}
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Exports</span>
          <span className="kpi-card__value">{kpis.exports}</span>
          <span className="dashboard__kpi-note">recent</span>
        </div>
      </div>

      <div className="dashboard__grid">
        <div className="dashboard__panel dashboard__jobs inspector">
          <div className="inspector__head">
            <h3>Mock-up queue</h3>
            <span className="faint">Local · {kpis.running} active</span>
          </div>
          <div className="inspector__body">
            {jobs.length === 0 ? (
              <EmptyState icon="list" size="sm" title="No jobs in range yet." />
            ) : (
              <div className="dashboard__queue">
                {jobs.map((j) => {
                  const done = j.items.length;
                  const total = j.designCount;
                  const pct = j.status === "completed"
                    ? 100
                    : total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                  const isDone = j.status === "completed";
                  return (
                    <div key={j.jobId} className="dashboard__queue-row" data-testid="dashboard-job" data-status={j.status}>
                      <div className="dashboard__queue-thumb" aria-hidden="true" />
                      <div className="dashboard__queue-meta">
                        <b className="dashboard__queue-name">
                          <code className="dashboard__job-id">{j.jobId}</code>
                          <CopyButton value={j.jobId} label="Copy job ID" className="dashboard__job-copy" />
                        </b>
                        <small className="dashboard__queue-sub">
                          {total > 0 ? `${done} / ${total} frames` : "Queued · draft template"}
                          {j.durationMs ? ` · ${fmtDuration(j.durationMs)}` : ""}
                        </small>
                      </div>
                      <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                        <i className="progress__bar" data-tone={isDone ? "success" : undefined} style={{ width: `${pct}%` }} />
                      </div>
                      <StatusPill status={j.status} />
                      <RowAction
                        label="Trace"
                        icon="search"
                        tone="quiet"
                        className="dashboard__queue-trace"
                        data-testid="dashboard-job-trace"
                        /* F-8: the seam G-26o opens the trace drawer through. */
                        data-gate-open="dashboard-trace"
                        onClick={() => openJob(j.jobId)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="dashboard__side">
          <div className="dashboard__panel dashboard__activity inspector">
            <div className="inspector__head">
              <h3>Activity</h3>
              <span className="faint">Audit</span>
            </div>
            <div className="inspector__body">
              {feed.length === 0 ? (
                <EmptyState icon="list" size="sm" title="No activity yet." />
              ) : (
                <div className="dashboard__feed">
                  {feed.map((l) => (
                    <button
                      type="button"
                      key={l.id}
                      className="dashboard__feed-row dashboard__feed-row--btn"
                      data-testid="dashboard-feed-row"
                      data-gate-open="dashboard-trace"
                      onClick={() => openJob(l.id)}
                      title="View trace"
                    >
                      <span className={`dashboard__feed-dot dashboard__feed-dot--${l.tone}`} aria-hidden="true"><Icon name="dot" size={16} /></span>
                      <span className="dashboard__feed-time">{fmtAgo(l.when, lastUpdated ?? Date.now())}</span>
                      {" · "}
                      {l.text} {l.code && <b>{l.code}</b>}
                    </button>
                  ))}
                </div>
              )}
              {/* Was `.dashboard__audit-btn`: twelve declarations re-implementing `.btn` at its
                  own height, radius and label size. It is a full-width toolbar action. */}
              <BusyButton
                className="btn btn--sm btn--block"
                onAction={() => loadActivity()}
                data-testid="dashboard-audit"
              >
                View full audit
              </BusyButton>
            </div>
          </div>

          <div className="dashboard__panel dashboard__live inspector">
            <div className="inspector__head">
              <h3>Live desktops</h3>
              <span className="faint">{kpis.online}/{heartbeats.length} online</span>
            </div>
            <div className="inspector__body">
              {heartbeats.length === 0 ? (
                <EmptyState icon="phone" size="sm" title="No desktops reporting." />
              ) : (
                <ul className="dashboard__desktops">
                  {heartbeats.map((h) => (
                    <li key={h.deviceId} className="dashboard__desktop" data-online={h.online}>
                      <div className="dashboard__desktop-head">
                        <span className="dashboard__dot" data-online={h.online} aria-hidden="true" />
                        {/* WS-B B-3 / AUD-6 MIN-8: this printed the raw 64-char SHA-256 as the
                            device NAME. The full id stays recoverable in the title — support
                            needs it, the operator does not need to read it. */}
                        <span className="dashboard__desktop-id" title={h.deviceId}>{deviceLabel(h.deviceId)}</span>
                        <span className="faint dashboard__desktop-seen">{fmtAgo(h.ts, lastUpdated ?? Date.now())}</span>
                      </div>
                      <div className="dashboard__desktop-meta">
                        {h.activeJobId ? (
                          <span>
                            Job <code>{h.activeJobId}</code>
                            {typeof h.progress === "number" ? ` · ${Math.round(h.progress * 100)}%` : ""}
                          </span>
                        ) : (
                          <span className="faint">{h.online ? "Idle" : "Offline"}</span>
                        )}
                        <span className="faint">
                          {h.appVersion ? `app ${h.appVersion}` : ""}
                          {h.engineVersion ? ` · engine ${h.engineVersion}` : ""}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="dashboard__panel dashboard__exports inspector">
            <div className="inspector__head">
              <h3>Exports</h3>
              <span className="faint">{exportsList.length} recent</span>
            </div>
            <div className="inspector__body">
              {exportsList.length === 0 ? (
                <EmptyState icon="download" size="sm" title="No exports yet." />
              ) : (
                <ul className="dashboard__export-list">
                  {exportsList.map((x, i) => (
                    <li key={`${x.batchId}:${i}`} className="dashboard__export">
                      <div className="dashboard__export-head">
                        <span className="dashboard__export-name">{x.designName}</span>
                        <span
                          className={`badge ${x.status === "ok" ? "badge--success" : x.status === "missing" ? "badge--warn" : "badge--danger"}`}
                        >
                          {enumLabel(x.status)}
                        </span>
                      </div>
                      <span className="faint">
                        {x.view} · {x.format.toUpperCase()} · ×{x.count}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {isSuper && (
            <div className="dashboard__panel dashboard__tenants inspector" data-testid="dashboard-tenants">
              <div className="inspector__head">
                <h3>Tenant summary</h3>
                <span className="faint">{stores.length} workspaces</span>
              </div>
              <div className="inspector__body">
                {stores.length === 0 ? (
                  <EmptyState icon="admin" size="sm" title="No tenant workspaces yet." />
                ) : (
                  <div className="dashboard__table-scroll">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Tier</th>
                          <th className="dashboard__num">Devices</th>
                          <th className="dashboard__num">Jobs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stores.slice(0, TENANT_SUMMARY_LIMIT).map((s) => (
                          <tr key={s.customer_id} data-testid="dashboard-tenant-row">
                            <td className="dashboard__tenant-email">{s.email}</td>
                            <td>{s.tier || "—"}</td>
                            <td className="dashboard__num">{s.device_count}</td>
                            <td className="dashboard__num">{s.job_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {stores.length > TENANT_SUMMARY_LIMIT && (
                      <p className="faint dashboard__tenants-more">
                        +{stores.length - TENANT_SUMMARY_LIMIT} more in Customers
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* WS-Z4 / R1: the trace drill-down is the SHARED drawer. It was a hand-rolled
          `.lightbox` carrying `role="dialog" aria-modal` — one of four modal/drawer
          vocabularies, and `.lightbox` is image zoom, which is why it had no Escape key and
          no focus move. Its own "Close" button is gone; the drawer's close control is the
          survivor, and it closes on Escape and on the overlay too. */}
      {selected && (
        <Drawer
          title={<>Trace — <code>{selected.job.jobId.slice(0, 8)}</code></>}
          subtitle={enumLabel(selected.job.status)}
          onClose={() => setSelected(null)}
          testid="dashboard-trace"
        >
          {selected.traces.length === 0 ? (
            <p className="faint">No trace events recorded for this job.</p>
          ) : (
            <ol className="obs-trace__list">
              {selected.traces.map((t, i) => (
                <li key={i} className={`obs-trace__evt obs-trace__evt--${t.level}`}>
                  <span className="obs-trace__phase">{t.phase}{t.itemKey ? ` · ${t.itemKey}` : ""}</span>
                  <span className="obs-trace__msg">{t.message}</span>
                  <span className="obs-trace__time faint">{t.endedAt ? fmtDuration(t.endedAt - t.startedAt) : "…"}</span>
                </li>
              ))}
            </ol>
          )}
        </Drawer>
      )}
    </section>
  );
}
